// src/components/game/JigsawGame.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const JigsawGame = ({ isDarkMode }) => {
  const [pieces, setPieces] = useState(Array.from({ length: 16 }, (_, i) => i));
  const [isSolved, setIsSolved] = useState(false);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    const saved = localStorage.getItem('jigsawBestScore');
    return saved ? parseInt(saved) : 0;
  });

  useEffect(() => {
    let timer;
    if (isPlaying && !isSolved) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, isSolved]);

  const shufflePieces = () => {
    const shuffled = [...pieces];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setPieces(shuffled);
    setIsSolved(false);
    setMoves(0);
    setTime(0);
    setIsPlaying(true);
  };

  const checkSolution = () => {
    const sorted = [...pieces].sort((a, b) => a - b);
    if (JSON.stringify(pieces) === JSON.stringify(sorted)) {
      setIsSolved(true);
      setIsPlaying(false);
      if (time < bestScore || bestScore === 0) {
        setBestScore(time);
        localStorage.setItem('jigsawBestScore', time.toString());
      }
    }
  };

  const swapPieces = (index) => {
    if (isSolved || !isPlaying) return;
    if (index < pieces.length - 1) {
      const newPieces = [...pieces];
      [newPieces[index], newPieces[index + 1]] = [newPieces[index + 1], newPieces[index]];
      setPieces(newPieces);
      setMoves(moves + 1);
      setTimeout(checkSolution, 100);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ფერები
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
    '#FFEAA7', '#DDA0DD', '#FF9FF3', '#54A0FF',
    '#5F27CD', '#FF6348', '#7BED9F', '#70A1FF',
    '#FF4757', '#2ED573', '#FFA502', '#1E90FF'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: isDarkMode 
        ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
        : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <Link to="/games" style={{
        padding: '12px 28px',
        margin: '10px 0',
        background: isDarkMode 
          ? 'rgba(255,255,255,0.1)' 
          : 'rgba(0,0,0,0.05)',
        color: isDarkMode ? '#fff' : '#000',
        textDecoration: 'none',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
        transition: 'all 0.3s',
        fontWeight: '500'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateX(-5px)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateX(0)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
      }}>
        ← თამაშებში დაბრუნება
      </Link>

      <div style={{
        maxWidth: '600px',
        width: '100%',
        padding: '30px',
        background: isDarkMode 
          ? 'rgba(26, 26, 46, 0.9)' 
          : 'rgba(255, 255, 255, 0.9)',
        borderRadius: '20px',
        backdropFilter: 'blur(20px)',
        boxShadow: isDarkMode 
          ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
          : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
        animation: 'slideUp 0.6s ease-out'
      }}>
        <style>
          {`
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(30px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
            @keyframes shimmer {
              0% { background-position: -200% center; }
              100% { background-position: 200% center; }
            }
            .game-title {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-size: 200% auto;
              animation: shimmer 3s linear infinite;
            }
          `}
        </style>

        <h1 style={{ 
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          marginBottom: '5px'
        }}>
          <span className="game-title">🧩 Jigsaw Puzzle</span>
        </h1>
        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666', 
          textAlign: 'center',
          fontSize: 'clamp(12px, 1.5vw, 16px)'
        }}>
          დაალაგე ნაწილები სწორად!
        </p>

        {/* სტატისტიკა */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
          gap: '10px',
          margin: '20px 0',
          padding: '15px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: '20px', color: '#4ECDC4' }}>🔄</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{moves}</div>
            <div style={{ fontSize: '10px', color: isDarkMode ? '#666' : '#999' }}>ნაბიჯი</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', color: '#FF6B6B' }}>⏱️</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{formatTime(time)}</div>
            <div style={{ fontSize: '10px', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', color: '#FFD93D' }}>🏆</div>
            <div style={{ fontSize: '14px', color: isDarkMode ? '#fff' : '#000' }}>{bestScore ? formatTime(bestScore) : '--'}</div>
            <div style={{ fontSize: '10px', color: isDarkMode ? '#666' : '#999' }}>საუკეთესო</div>
          </div>
        </div>

        {/* თავსატეხი */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px',
          margin: '20px 0',
          padding: '12px',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
          borderRadius: '16px',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          {pieces.map((num, index) => (
            <div
              key={index}
              onClick={() => swapPieces(index)}
              style={{
                aspectRatio: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'clamp(18px, 3vw, 28px)',
                fontWeight: 'bold',
                borderRadius: '12px',
                cursor: isSolved || !isPlaying ? 'default' : 'pointer',
                background: isSolved 
                  ? 'linear-gradient(135deg, #4ade80, #22d3ee)' 
                  : num === index 
                    ? `linear-gradient(135deg, ${colors[num % colors.length]}, ${colors[(num + 1) % colors.length]})`
                    : isDarkMode 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.05)',
                color: isSolved || num === index ? '#fff' : (isDarkMode ? '#444' : '#ccc'),
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: isSolved ? 'scale(1.05)' : 'scale(1)',
                boxShadow: isSolved 
                  ? '0 4px 20px rgba(74,222,128,0.4)' 
                  : num === index 
                    ? `0 4px 15px ${colors[num % colors.length]}40` 
                    : 'none',
                animation: isSolved ? 'pulse 1s infinite' : 'none'
              }}
            >
              {num + 1}
            </div>
          ))}
        </div>

        {isSolved && (
          <div style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            borderRadius: '12px',
            color: '#fff',
            textAlign: 'center',
            marginBottom: '20px',
            animation: 'pulse 1.5s infinite',
            fontWeight: 'bold',
            fontSize: 'clamp(14px, 2vw, 20px)',
            boxShadow: '0 4px 30px rgba(74,222,128,0.3)'
          }}>
            🎉 გილოცავთ! თავსატეხი ამოხსნილია! 🎉
            <br />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>
              {moves} ნაბიჯი • {formatTime(time)} წამი
            </span>
          </div>
        )}

        <button
          onClick={shufflePieces}
          style={{
            width: '100%',
            padding: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            fontSize: 'clamp(16px, 2vw, 18px)',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(102,126,234,0.3)',
            letterSpacing: '0.5px'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px) scale(1.02)';
            e.target.style.boxShadow = '0 8px 30px rgba(102,126,234,0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 20px rgba(102,126,234,0.3)';
          }}
        >
          🔀 ახალი თამაში
        </button>

        {/* მობილური რჩევა */}
        <div style={{
          marginTop: '15px',
          padding: '12px',
          textAlign: 'center',
          fontSize: '12px',
          color: isDarkMode ? '#666' : '#999',
          borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          💡 დააკლიკე რიცხვს მის მარჯვნივ გადასაადგილებლად
        </div>
      </div>
    </div>
  );
};

export default JigsawGame;